import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from 'v5/platform/dom/cn';

const badgeVariants = cva(
  'inline-flex items-center rounded border font-semibold transition-colors',
  {
    variants: {
      variant: {
        info: '',
        success: '',
        danger: '',
        attention: '',
        accent: '',
        preview: '',
        brand: '',
        gray: '',
        /** A faint tint of the text colour, whatever the tone: reads on any surface, a picture included. */
        tint: 'border-transparent bg-fg-default/10 text-fg-default not-aria-readonly:hocus:bg-fg-default/15',
        bubblegum: '',
        pear: '',
        rose: '',
        sun: '',
        variable: ''
      },
      tone: {
        solid: '',
        subtle: ''
      },
      size: {
        sm: 'py-0.5 text-xs [&>svg]:size-3 gap-1',
        md: 'py-0.5 text-sm [&>svg]:size-4 gap-1',
        lg: 'py-0.5 text-base [&>svg]:size-5 gap-1'
      },
      /**
       * `icon`: a square badge holding only an icon (give it an `aria-label`).
       * `dot`: the colour alone, where a word doesn't fit; the text stays for screen readers.
       */
      layout: {
        normal: '',
        icon: 'shrink-0 justify-center px-0',
        dot: 'shrink-0 rounded-full border-0 p-0'
      },
      /** A dashed outline on no ground: something that stands for "any", not a value. */
      dashed: {
        true: '',
        false: ''
      }
    },
    compoundVariants: [
      {
        layout: 'normal',
        size: 'sm',
        className:
          'px-2.5 [&:has(svg:first-child)]:pl-1.5 [&:has(svg:last-child)]:pr-1.5'
      },
      {
        layout: 'normal',
        size: 'md',
        className:
          'px-3 [&:has(svg:first-child)]:pl-1.5 [&:has(svg:last-child)]:pr-1.5'
      },
      {
        layout: 'normal',
        size: 'lg',
        className:
          'px-4 [&:has(svg:first-child)]:pl-1.5 [&:has(svg:last-child)]:pr-1.5'
      },
      // As tall as a text badge of the size: line height + padding + border.
      { layout: 'icon', size: 'sm', className: 'size-[calc(1.25rem+2px)]' },
      { layout: 'icon', size: 'md', className: 'size-[calc(1.5rem+2px)]' },
      { layout: 'icon', size: 'lg', className: 'size-[calc(1.75rem+2px)]' },
      { layout: 'dot', size: 'sm', className: 'size-2' },
      { layout: 'dot', size: 'md', className: 'size-2.5' },
      { layout: 'dot', size: 'lg', className: 'size-3' },
      // Default variant - subtle tones
      {
        variant: 'info',
        tone: 'subtle',
        className: [
          'bg-bg-info',
          'text-fg-info',
          'not-aria-readonly:hocus:bg-border-info',
          'border-transparent'
        ]
      },
      {
        variant: 'success',
        tone: 'subtle',
        className: [
          'bg-bg-success',
          'text-fg-success',
          'not-aria-readonly:hocus:bg-border-success',
          'border-transparent'
        ]
      },
      {
        variant: 'danger',
        tone: 'subtle',
        className: [
          'bg-bg-danger',
          'text-fg-danger',
          'not-aria-readonly:hocus:bg-border-danger',
          'border-transparent'
        ]
      },
      {
        variant: 'attention',
        tone: 'subtle',
        className: [
          'bg-bg-attention',
          'text-fg-attention',
          'not-aria-readonly:hocus:bg-border-attention',
          'border-transparent'
        ]
      },
      {
        variant: 'accent',
        tone: 'subtle',
        className: [
          'bg-bg-accent',
          'text-fg-accent',
          'not-aria-readonly:hocus:bg-border-accent',
          'border-transparent'
        ]
      },
      {
        variant: 'preview',
        tone: 'subtle',
        className: [
          'bg-bg-preview',
          'text-fg-preview',
          'not-aria-readonly:hocus:bg-border-preview',
          'border-transparent'
        ]
      },
      {
        variant: 'brand',
        tone: 'subtle',
        className: [
          'bg-brand-100',
          'text-brand-700',
          'not-aria-readonly:hocus:bg-brand-200',
          'border-transparent'
        ]
      },
      {
        variant: 'gray',
        tone: 'subtle',
        className: [
          'bg-bg-subtle',
          'text-fg-default',
          'not-aria-readonly:hocus:bg-bg-subtle-2',
          'border-transparent'
        ]
      },
      {
        variant: 'bubblegum',
        tone: 'subtle',
        className: [
          'bg-bubblegum-light',
          'text-white',
          'not-aria-readonly:hocus:bg-bubblegum',
          'border-transparent'
        ]
      },
      {
        variant: 'pear',
        tone: 'subtle',
        className: [
          'bg-pear-light',
          'text-white',
          'not-aria-readonly:hocus:bg-pear',
          'border-transparent'
        ]
      },
      {
        variant: 'rose',
        tone: 'subtle',
        className: [
          'bg-rose-light',
          'text-white',
          'not-aria-readonly:hocus:bg-rose',
          'border-transparent'
        ]
      },
      {
        variant: 'sun',
        tone: 'subtle',
        className: [
          'bg-sun-light',
          'text-white',
          'not-aria-readonly:hocus:bg-sun',
          'border-transparent'
        ]
      },

      // Default variant - solid tones
      {
        variant: 'info',
        tone: 'solid',
        className: [
          'bg-bg-info-emphasis',
          'text-white',
          'not-aria-readonly:hocus:bg-bg-info-emphasis-2',
          'border-transparent'
        ]
      },
      {
        variant: 'success',
        tone: 'solid',
        className: [
          'bg-bg-success-emphasis',
          'text-white',
          'not-aria-readonly:hocus:bg-bg-success-emphasis-2',
          'border-transparent'
        ]
      },
      {
        variant: 'danger',
        tone: 'solid',
        className: [
          'bg-bg-danger-emphasis',
          'text-white',
          'not-aria-readonly:hocus:bg-bg-danger-emphasis-2',
          'border-transparent'
        ]
      },
      {
        variant: 'attention',
        tone: 'solid',
        className: [
          'bg-bg-attention-emphasis',
          'text-white',
          'not-aria-readonly:hocus:bg-bg-attention-emphasis-2',
          'border-transparent'
        ]
      },
      {
        variant: 'accent',
        tone: 'solid',
        className: [
          'bg-bg-accent-emphasis',
          'text-white',
          'not-aria-readonly:hocus:bg-bg-accent-emphasis-2',
          'border-transparent'
        ]
      },
      {
        variant: 'preview',
        tone: 'solid',
        className: [
          'bg-bg-preview-emphasis',
          'text-white',
          'not-aria-readonly:hocus:bg-bg-preview-emphasis-2',
          'border-transparent'
        ]
      },
      {
        variant: 'brand',
        tone: 'solid',
        className: [
          'bg-brand-500',
          'text-white',
          'not-aria-readonly:hocus:bg-brand-600',
          'border-transparent'
        ]
      },
      {
        variant: 'gray',
        tone: 'solid',
        className: [
          'bg-charcoal-light',
          'text-white',
          'not-aria-readonly:hocus:bg-charcoal',
          'border-transparent'
        ]
      },
      {
        variant: 'bubblegum',
        tone: 'solid',
        className: [
          'bg-bubblegum',
          'text-white',
          'not-aria-readonly:hocus:bg-bubblegum-dark',
          'border-transparent'
        ]
      },
      {
        variant: 'pear',
        tone: 'solid',
        className: [
          'bg-pear',
          'text-white',
          'not-aria-readonly:hocus:bg-pear-dark',
          'border-transparent'
        ]
      },
      {
        variant: 'rose',
        tone: 'solid',
        className: [
          'bg-rose',
          'text-white',
          'not-aria-readonly:hocus:bg-rose-dark',
          'border-transparent'
        ]
      },
      {
        variant: 'sun',
        tone: 'solid',
        className: [
          'bg-sun',
          'text-white',
          'not-aria-readonly:hocus:bg-sun-dark',
          'border-transparent'
        ]
      },
      {
        variant: 'variable',
        tone: 'subtle',
        className: [
          'bg-[color-mix(in_srgb,var(--badge-color)_16%,transparent)]',
          'text-[color-mix(in_srgb,var(--badge-color)_72%,black)]',
          'not-aria-readonly:hocus:bg-[color-mix(in_srgb,var(--badge-color)_24%,transparent)]',
          'border-[color-mix(in_srgb,var(--badge-color)_35%,transparent)]'
        ]
      },
      {
        variant: 'variable',
        tone: 'solid',
        className: [
          'bg-[var(--badge-color)]',
          'text-white',
          'not-aria-readonly:hocus:bg-[color-mix(in_srgb,var(--badge-color)_88%,black)]',
          'border-[color-mix(in_srgb,var(--badge-color)_60%,black)]'
        ]
      },
      // last, so its border and ground win over the tone's
      {
        dashed: true,
        className:
          'border-dashed border-border-default bg-transparent not-aria-readonly:hocus:bg-bg-subtle'
      }
    ],
    defaultVariants: {
      variant: 'accent',
      tone: 'solid',
      size: 'sm',
      layout: 'normal'
    }
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  asChild?: boolean;
}

export type BadgeVariant = VariantProps<typeof badgeVariants>['variant'];
export type BadgeTone = VariantProps<typeof badgeVariants>['tone'];
export type BadgeSize = VariantProps<typeof badgeVariants>['size'];

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      className,
      variant,
      tone,
      size,
      layout,
      dashed,
      asChild,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'div';

    return (
      <Comp
        ref={ref}
        className={cn(
          badgeVariants({ variant, tone, size, layout, dashed }),
          className
        )}
        {...props}
      >
        {layout === 'dot' && !asChild ? (
          <span className="sr-only">{children}</span>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
